import React from 'react';
import { Zap, Diamond, Check } from 'lucide-react';
import { SectionDivider } from '../SectionDivider';
interface IntegrationComparisonProps {
  'data-id'?: string;
}
export function IntegrationComparison({
  'data-id': dataId
}: IntegrationComparisonProps) {
  return <>
      <SectionDivider from="from-white" to="to-gray-50" height="h-16" data-id="element-336" />
      <section className="w-full relative pt-16 pb-24 px-6 overflow-hidden bg-gray-50" data-id="element-337">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden" data-id="element-338">
          <div className="absolute top-0 right-0 w-full h-full" data-id="element-339">
            <div className="absolute -top-[10%] -right-[10%] w-[40%] h-[40%] rounded-full opacity-[0.04] blur-[80px]" style={{
            background: 'radial-gradient(circle, rgba(0,136,96,0.6) 0%, rgba(255,255,255,0) 70%)'
          }} data-id="element-340"></div>
            <div className="absolute bottom-[20%] left-[5%] w-[30%] h-[30%] rounded-full opacity-[0.03] blur-[60px]" style={{
            background: 'radial-gradient(circle, rgba(0,87,184,0.5) 0%, rgba(255,255,255,0) 70%)'
          }} data-id="element-341"></div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-342">
          {/* Header */}
          <div className="mx-auto max-w-2xl sm:text-center mb-16" data-id="element-343">
            <p className="mt-2 text-3xl font-normal tracking-tight text-benetton-green sm:text-4xl font-apercu" data-id="element-344">
              Two Simple Ways to Connect
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600 font-jakarta" data-id="element-345">
              Flexible integration options: Route through Klaviyo or use our
              direct app connections. New integrations rolling out daily.
            </p>
          </div>
          {/* Cards Container */}
          <div className="grid md:grid-cols-2 gap-8 max-w-9xl mx-auto" data-id="element-346">
            {/* Path 1 */}
            <div className="bg-white rounded-lg shadow-lg border border-benetton-green p-8 relative" data-id="element-347">
              <div className="mb-4" data-id="element-348">
                <span className="bg-benetton-blue text-white px-3 py-1 rounded text-sm font-medium mb-12" data-id="element-349">
                  LIVE NOW
                </span>
              </div>
              <div className="flex items-center gap-3 mb-2" data-id="element-350">
                <div className="w-8 h-8 bg-benetton-blue rounded-lg flex items-center justify-center" data-id="element-351">
                  <img src="https://cdn-icons-png.flaticon.com/512/6831/6831718.png" alt="Lightning bolt" className="w-5 h-5 grayscale" data-id="element-352" />
                </div>
                <h3 className="text-heading-3 font-normal text-benetton-green font-apercu" data-id="element-353">
                  Path 1: Klaviyo Hub
                </h3>
              </div>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed pt-4 font-jakarta" data-id="element-354">
                Already using Klaviyo? Get instant access to a range of
                connected Shopify apps by connecting through your existing
                Klaviyo account. Connect the rest via our integration hub.
              </p>
              <div className="space-y-3" data-id="element-355">
                <div className="flex items-center gap-3" data-id="element-356">
                  <Check className="w-5 h-5 text-black" data-id="element-357" />
                  <span className="text-lg text-gray-700 font-jakarta" data-id="element-358">
                    Instant setup with existing Klaviyo account
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-359">
                  <Check className="w-5 h-5 text-black" data-id="element-360" />
                  <span className="text-lg text-gray-700 font-jakarta" data-id="element-361">
                    Access to data from 100+ integrated apps within Klaviyo
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-362">
                  <Check className="w-5 h-5 text-black" data-id="element-363" />
                  <span className="text-lg text-gray-700 font-jakarta" data-id="element-364">
                    No additional API configuration required
                  </span>
                </div>
              </div>
            </div>
            {/* Path 2 */}
            <div className="bg-white rounded-lg shadow-lg border border-benetton-green p-8 relative" data-id="element-365">
              <div className="mb-4" data-id="element-366">
                <span className="bg-benetton-green text-white px-3 py-1 rounded text-sm font-medium mb-4" data-id="element-367">
                  BUILDING
                </span>
              </div>
              <div className="flex items-center gap-3 mb-6" data-id="element-368">
                <div className="w-8 h-8 bg-benetton-green rounded-lg flex items-center justify-center" data-id="element-369">
                  <img src="https://cdn-icons-png.flaticon.com/512/6295/6295417.png" alt="Diamond" className="w-5 h-5 grayscale" data-id="element-370" />
                </div>
                <h3 className="text-heading-3 font-normal text-benetton-green font-apercu" data-id="element-371">
                  Path 2: Naiomi Integrations
                </h3>
              </div>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed font-jakarta" data-id="element-372">
                Direct access to our growing integration hub. Connect your
                existing apps today, explore new platforms launching daily, or
                vote for the next apps we should add.
              </p>
              <div className="space-y-3" data-id="element-373">
                <div className="flex items-center gap-3" data-id="element-374">
                  <Check className="w-5 h-5 text-benetton-green" data-id="element-375" />
                  <span className="text-lg text-gray-700 font-jakarta" data-id="element-376">
                    Direct connections to your existing tech stack
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-377">
                  <Check className="w-5 h-5 text-benetton-green" data-id="element-378" />
                  <span className="text-lg text-gray-700 font-jakarta" data-id="element-379">
                    New integrations launching daily
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-380">
                  <Check className="w-5 h-5 text-benetton-green" data-id="element-381" />
                  <span className="text-lg text-gray-700 font-jakarta" data-id="element-382">
                    Request and vote for new apps
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <SectionDivider from="from-gray-50" to="to-white" height="h-16" data-id="element-383" />
    </>;
}